import React, { useState } from "react";
import {
  Box, Container, SimpleGrid, Image, Text, Heading, Stack, Badge, Button,
  Flex, useColorModeValue, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, useDisclosure, Input, 
  InputGroup, InputRightElement, useToast, VStack
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";

const MotionBox = motion(Box);

const CardSlider = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // --- ALL HOOKS CALLED AT TOP LEVEL (STRICT) ---
  const cardBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const modalBg = useColorModeValue("white", "gray.900");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const inputBg = useColorModeValue("gray.50", "whiteAlpha.100");
  // ----------------------------------------------

  const courses = [
    {
      id: 1,
      title: "Data Science Explorer",
      subtitle: "Unleashing Insights Through Bootcamp",
      image: "https://www.iimtindia.net/Blog/wp-content/uploads/2021/08/Data-Science-Future-scope.jpg",
      date: "Coming Soon",
      description: "Dive into the exciting world of data science. Gain hands-on experience, unlock valuable insights, and embark on a transformative journey in the realm of data analytics.",
      color: "blue.400",
    },
    {
      id: 2,
      title: "MERN Stack Maestro",
      subtitle: "Full-Stack Web Development Journey",
      image: "https://wallpapercave.com/wp/wp8903936.jpg",
      date: "Coming Soon",
      description: "Elevate your web development skills. Master MongoDB, Express, React, and Node.js to create dynamic and interactive web applications.",
      color: "green.400",
    },
    {
      id: 3,
      title: "Java Mastery",
      subtitle: "Ignite Your Career with Core Java",
      image: "https://wallpapercave.com/wp/wp7250212.jpg",
      date: "15-08-2025",
      description: "Immerse yourself in the power of Java, master the fundamentals, and embark on a transformative coding journey.",
      color: "red.400",
    },
    {
      id: 4,
      title: "DevOps Dynamo",
      subtitle: "Continuous Integration & Delivery",
      image: "https://img.freepik.com/free-vector/hand-drawn-flat-design-devops-illustration_23-2149387396.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      date: "Coming Soon",
      description: "Revolutionize software practices. Master cutting-edge tools and methodologies for seamless software delivery.",
      color: "purple.400",
    },
    {
      id: 5,
      title: "Testing Trailblazer",
      subtitle: "Core Software Testing Adventure",
      image: "https://img.freepik.com/free-vector/coding-round-composition_1284-40752.jpg?size=626&ext=jpg&ga=GA1.1.136392163.1683044531&semt=ais",
      date: "Coming Soon",
      description: "Ensure software quality and deliver flawless user experiences. Master industry-leading methodologies.",
      color: "orange.400",
    },
    {
      id: 6,
      title: "Interview Success Navigator",
      subtitle: "Comprehensive Prep Guide",
      image: "https://img.freepik.com/free-vector/isometric-characters-illustration-human-resources-doing-interview_1284-24421.jpg?size=626&ext=jpg&ga=GA1.2.136392163.1683044531&semt=ais",
      date: "Coming Soon",
      description: "Conquer your fears and land your dream job. Master essential techniques to excel in any interview scenario.",
      color: "pink.400",
    }
  ];

  const handleOpenModal = (course) => {
    setSelectedCourse(course);
    onOpen();
  };

  const handleNotify = () => {
    toast({
      title: "Waitlist Joined!",
      description: "We'll notify you once the batch is live.",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right"
    });
    onClose();
  };

  return (
    <Container maxW="7xl" py={20}>
      <Stack spacing={4} textAlign="center" mb={16}>
        <Badge variant="outline" colorScheme="blue" alignSelf="center" px={4} py={1} borderRadius="full">
          UPCOMING BOOTCAMPS
        </Badge>
        <Heading fontFamily="Bricolage Grotesque" size="2xl" fontWeight="900" color={headingColor}>
          The Next Wave of Learning.
        </Heading>
        <Text color={subTextColor} fontSize="lg">Pick a track. Join the waitlist. Get ahead.</Text>
      </Stack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
        {courses.map((course) => (
          <MotionBox
            key={course.id}
            whileHover={{ y: -12 }}
            transition={{ duration: 0.3 }}
            onClick={() => handleOpenModal(course)}
            bg={cardBg}
            borderRadius="3xl"
            overflow="hidden"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="2xl"
            cursor="pointer"
            role="group"
          >
            <Box h="220px" overflow="hidden">
              <Image
                src={course.image}
                w="full"
                h="full"
                objectFit="cover"
                transition="0.6s ease"
                _groupHover={{ transform: "scale(1.1)" }}
              />
            </Box>

            <Stack p={8} spacing={4}>
              <Flex align="center" gap={2}>
                <Box boxSize="8px" borderRadius="full" bg={course.color} />
                <Text fontSize="xs" fontWeight="black" color="gray.500" letterSpacing="widest">
                  {course.date}
                </Text>
              </Flex>
              <Box h="90px">
                <Heading size="md" fontWeight="800" mb={2} color={headingColor} noOfLines={2}>
                  {course.title}
                </Heading>
                <Text fontSize="sm" color={subTextColor} noOfLines={2}>
                  {course.subtitle}
                </Text>
              </Box>
              <Button 
                rightIcon={<FiArrowRight />} 
                variant="link" 
                color={course.color}
                fontSize="sm"
                fontWeight="bold"
                _groupHover={{ pl: 2 }}
                transition="0.3s"
              >
                View Roadmap
              </Button>
            </Stack>
          </MotionBox>
        ))}
      </SimpleGrid>

      {/* Info Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay backdropFilter="blur(15px)" bg="blackAlpha.800" />
        <ModalContent borderRadius="3xl" overflow="hidden" bg={modalBg}>
          <ModalCloseButton borderRadius="full" bg="whiteAlpha.400" zIndex={10} color="white" />
          
          {selectedCourse && (
            <>
              <Image src={selectedCourse.image} h="280px" objectFit="cover" />
              <ModalBody py={8} px={8}>
                <Stack spacing={6}>
                  <VStack align="flex-start" spacing={1}>
                    <Badge colorScheme="blue" borderRadius="md" px={2}>BOOTCAMP 2026</Badge>
                    <Heading size="xl" fontWeight="900" color={headingColor}>{selectedCourse.title}</Heading>
                  </VStack>

                  <Text fontSize="md" color={subTextColor} lineHeight="tall">
                    {selectedCourse.description}
                  </Text>

                  <Box pt={4}>
                    <Text fontWeight="bold" mb={3} fontSize="sm">JOIN THE WAITLIST</Text>
                    <InputGroup size="lg">
                      <Input 
                        placeholder="yourname@email.com" 
                        borderRadius="xl"
                        fontSize="md"
                        bg={inputBg}
                        border="none"
                      />
                      <InputRightElement width="7.5rem">
                        <Button h="2rem" size="sm" colorScheme="blue" borderRadius="lg" onClick={handleNotify}>
                          Notify Me
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                </Stack>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CardSlider;